import {
  ComposedChart,
  Area,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import type { TooltipContentProps } from "recharts";
import { useTranslation } from "react-i18next";
import type {
  DateWindow,
  MyBrandKpiCharts,
  KpiTimeSeries,
  MyBrandKpiCard,
  MyBrandKpiMetric,
  NorthStarKpi,
} from "@nexus/contracts";
import type { MyBrandKpiCardsState } from "../MyBrandKpiCards/MyBrandKpiCards.js";
import { formatMetricValue } from "../../lib/format";
import { useTenantCurrency } from "@/lib/use-tenant-currency";
import {
  CARD_DESCRIPTORS,
  type CardDescriptor,
  type CatalogColorEntry,
  resolveDashboardColorToken,
} from "../../lib/metrics";
import { applyCardOrder, DEFAULT_CARD_ORDER } from "../../lib/card-prefs";
import {
  computePeriodBuckets,
  computeChartTitle,
  bucketIndexForOffset,
  type PeriodBucket,
} from "../../lib/periodBuckets";
import { CHART_AXIS_TICK, CHART_GRID_STROKE, KPI_CHART_HEIGHT } from "../../lib/kpi-chart-style";
import { KpiChartCard } from "../KpiChartCard/KpiChartCard";
import { KpiChartCardMessage } from "../KpiChartCard/KpiChartCardMessage";
import { KpiChartCardSkeleton } from "../KpiChartCard/KpiChartCardSkeleton";

// ─── Types ────────────────────────────────────────────────────────────────────

interface MyBrandKpiChartProps {
  state: MyBrandKpiCardsState;
  charts?: MyBrandKpiCharts;
  northStar: NorthStarKpi;
  selectedCards: Set<number>;
  cardOrder?: readonly number[];
  window: DateWindow;
  catalogById?: Readonly<Record<string, CatalogColorEntry>>;
}

type ChartRow = Record<string, number | string> & { date: string };

/** Resolve the time series for a metric from the standalone charts map.
 *  `winners` maps to the north-star-specific series; everything else is 1:1. */
function resolveSeries(
  charts: MyBrandKpiCharts,
  metricKey: MyBrandKpiMetric,
  northStar: NorthStarKpi,
): KpiTimeSeries | undefined {
  switch (metricKey) {
    case "spend":                 return charts.spend;
    case "northStarRoas":         return charts.northStarRoas;
    case "northStarCpa":          return charts.northStarCpa;
    case "northStarCustom":       return charts.northStarCustom;
    case "creativeVolume":        return charts.creativeVolume;
    case "creativeConcentration": return charts.creativeConcentration;
    case "stickRate":             return charts.stickRate;
    case "winners":
      return northStar === "roas"
        ? charts.winnersRoas
        : northStar === "cpa"
          ? charts.winnersCpa
          : charts.winnersCustom;
  }
}

/** Sides that get readable ticks, in selection order. A third column of ticks would
 *  cost more plot width than it buys back in legibility. */
const VISIBLE_AXIS_SIDES = ["left", "right"] as const;
const AXIS_WIDTH = 48;

function axisIdFor(desc: CardDescriptor): string {
  return `axis-${desc.index}`;
}

function formatAxisTick(value: number, format: MyBrandKpiCard["format"]): string {
  if (format === "ratio") return `${value.toFixed(1)}x`;
  if (format === "percent") return `${value.toFixed(0)}%`;
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return format === "count" ? String(Math.round(value)) : value.toFixed(1);
}

// ─── Custom tooltip ───────────────────────────────────────────────────────────

interface KpiChartTooltipProps extends TooltipContentProps<number, string> {
  northStar: NorthStarKpi;
  labelResolver: (key: string) => string;
  currencySymbol: string;
}

function KpiChartTooltip({ active, payload, label, northStar, labelResolver, currencySymbol }: KpiChartTooltipProps) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border bg-card p-3 text-xs shadow-md">
      <p className="mb-2 text-muted-foreground">{label != null ? labelResolver(String(label)) : ""}</p>
      {payload.map((entry) => {
        if (entry.value === undefined || entry.value === null) return null;
        const isCurrent = (entry.dataKey as string).endsWith("_cur");
        // metricKey is always a valid MyBrandKpiMetric — dataKeys are built from desc.metric()
        const metricKey = (entry.dataKey as string).replace(/_(?:cur|pre)$/, "") as MyBrandKpiMetric;
        const desc = CARD_DESCRIPTORS.find((d) => d.metric(northStar) === metricKey);
        if (!desc) return null;
        return (
          <div key={entry.dataKey as string} className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ background: entry.stroke }}
            />
            <span className="text-muted-foreground">
              {desc.index === 1 ? northStar.toUpperCase() : desc.label}
            </span>
            <span className="font-medium text-foreground">
              {formatMetricValue(entry.value as number, desc.format(northStar), currencySymbol)}
            </span>
            <span className="text-muted-foreground">
              ({isCurrent ? "Current" : "Previous"})
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function ChartLegend({
  activeDescs,
  northStar,
  catalogById,
}: {
  activeDescs: CardDescriptor[];
  northStar: NorthStarKpi;
  catalogById?: Readonly<Record<string, CatalogColorEntry>>;
}) {
  return (
    <div className="flex flex-wrap items-center gap-4">
      {activeDescs.map((desc) => (
        <span key={desc.index} className="flex items-center gap-1.5">
          <span
            className="inline-block h-[2px] w-3 shrink-0"
            style={{ background: `var(${resolveDashboardColorToken(desc, northStar, catalogById)})` }}
          />
          <span className="text-[12px] text-foreground/80">
            {desc.index === 1 ? northStar.toUpperCase() : desc.label}
          </span>
        </span>
      ))}
      {activeDescs.length > 0 && (
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-[2px] w-3 shrink-0 border-t-2 border-dashed border-muted-foreground/60" />
          <span className="text-[12px] text-muted-foreground">Previous period</span>
        </span>
      )}
    </div>
  );
}

// ─── Bucket aggregation ───────────────────────────────────────────────────────

type BucketEntry = { sum: number; count: number; isAverage: boolean };

/** Exported for its tests: how days roll up into the chart's buckets. */
export function buildChartData(
  activeDescs: CardDescriptor[],
  charts: MyBrandKpiCharts,
  northStar: NorthStarKpi,
  buckets: PeriodBucket[],
): ChartRow[] {
  const acc = new Map<string, Record<string, BucketEntry>>(
    buckets.map((b) => [b.key, {}]),
  );

  for (const desc of activeDescs) {
    const metricKey = desc.metric(northStar);
    const series    = resolveSeries(charts, metricKey, northStar);
    const isAverage = desc.aggregation === "average";

    const accumulate = (bucketKey: string, dataKey: string, value: number | null) => {
      // A day without a value (ROAS with no spend) is left out, not averaged in as zero.
      if (value === null) return;
      const entry = acc.get(bucketKey)!;
      if (!entry[dataKey]) entry[dataKey] = { sum: 0, count: 0, isAverage };
      entry[dataKey].sum   += value;
      entry[dataKey].count += 1;
    };

    for (const pt of series?.current ?? []) {
      const idx = bucketIndexForOffset(pt.dayOffset, buckets);
      accumulate(buckets[idx].key, `${metricKey}_cur`, pt.value);
    }
    for (const pt of series?.previous ?? []) {
      const idx = bucketIndexForOffset(pt.dayOffset, buckets);
      accumulate(buckets[idx].key, `${metricKey}_pre`, pt.value);
    }
  }

  return buckets.map((b) => {
    const row: ChartRow = { date: b.key };
    for (const [dk, { sum, count, isAverage }] of Object.entries(acc.get(b.key) ?? {})) {
      if (count > 0) {
        (row as Record<string, number | string>)[dk] = isAverage ? sum / count : sum;
      }
      // count === 0: key absent from row → Recharts sees undefined → connectNulls bridges it
    }
    return row;
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MyBrandKpiChart({
  state,
  charts,
  northStar,
  selectedCards,
  cardOrder,
  window,
  catalogById,
}: MyBrandKpiChartProps) {
  const { t } = useTranslation();
  const { symbol: currencySymbol } = useTenantCurrency();
  const order = cardOrder ?? DEFAULT_CARD_ORDER;
  const activeDescs = applyCardOrder(
    CARD_DESCRIPTORS.filter((d) => selectedCards.has(d.index)),
    order,
  );

  if (state === "empty" || state === "error") return null;

  // Loading skeleton — chart data loads independently of the KPI cards.
  if (!charts) return <KpiChartCardSkeleton />;

  if (activeDescs.length === 0) {
    return (
      <KpiChartCardMessage>
        {t("dashboard.myBrandChart.emptySelection", "Click a KPI card above to add it to the chart.")}
      </KpiChartCardMessage>
    );
  }

  const buckets      = computePeriodBuckets(window);
  const title        = computeChartTitle(window);
  const bucketLabels = new Map<string, string>(buckets.map((b) => [b.key, b.label]));
  const labelResolver = (key: string) => bucketLabels.get(key) ?? key;

  const chartData = buildChartData(activeDescs, charts, northStar, buckets);

  return (
    <KpiChartCard
      title={title}
      subtitle={t(
        "dashboard.myBrandChart.subtitle",
        "Click a KPI card above to add or remove it. Dashed lines show the previous period.",
      )}
      legend={<ChartLegend activeDescs={activeDescs} northStar={northStar} catalogById={catalogById} />}
    >
      <ResponsiveContainer width="100%" height={KPI_CHART_HEIGHT}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
          <defs>
            {activeDescs.map((desc) => (
              <linearGradient key={`grad-${desc.index}`} id={`grad-${desc.index}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={`var(${resolveDashboardColorToken(desc, northStar, catalogById)})`} stopOpacity={0.15} />
                <stop offset="100%" stopColor={`var(${resolveDashboardColorToken(desc, northStar, catalogById)})`} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={CHART_GRID_STROKE}
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tickFormatter={labelResolver}
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
          />
          {activeDescs.map((desc, index) => {
            const side = VISIBLE_AXIS_SIDES[index];
            // Past the second KPI the axis is scale-only: it still keeps the series off
            // every other KPI's domain, it just has no ticks to read.
            if (!side) {
              return (
                <YAxis
                  key={desc.index}
                  yAxisId={axisIdFor(desc)}
                  orientation="right"
                  width={0}
                  tick={false}
                  tickLine={false}
                  axisLine={false}
                />
              );
            }
            return (
              <YAxis
                key={desc.index}
                yAxisId={axisIdFor(desc)}
                orientation={side}
                width={AXIS_WIDTH}
                tickFormatter={(v: number) => formatAxisTick(v, desc.format(northStar))}
                tick={{
                  ...CHART_AXIS_TICK,
                  fill: `var(${resolveDashboardColorToken(desc, northStar, catalogById)})`,
                }}
                tickLine={false}
                axisLine={false}
              />
            );
          })}
          <Tooltip
            content={(tooltipProps) => (
              <KpiChartTooltip
                {...(tooltipProps as TooltipContentProps<number, string>)}
                northStar={northStar}
                labelResolver={labelResolver}
                currencySymbol={currencySymbol}
              />
            )}
          />

          {activeDescs.flatMap((desc) => {
            const metricKey = desc.metric(northStar);
            const color = `var(${resolveDashboardColorToken(desc, northStar, catalogById)})`;
            return [
              <Area
                key={`${metricKey}_cur`}
                yAxisId={axisIdFor(desc)}
                dataKey={`${metricKey}_cur`}
                stroke={color}
                strokeWidth={2}
                fill={`url(#grad-${desc.index})`}
                fillOpacity={1}
                dot={false}
                type="monotone"
                isAnimationActive={true}
                animationDuration={800}
                animationEasing="ease-out"
                connectNulls
              />,
              <Line
                key={`${metricKey}_pre`}
                yAxisId={axisIdFor(desc)}
                dataKey={`${metricKey}_pre`}
                stroke={color}
                strokeDasharray="5 5"
                strokeOpacity={0.5}
                strokeWidth={1.5}
                dot={false}
                type="monotone"
                isAnimationActive={true}
                animationDuration={800}
                animationEasing="ease-out"
                connectNulls
              />,
            ];
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </KpiChartCard>
  );
}
